/**
 * PricingPreviewPanel
 *
 * SCRUM-1287: Guide Dashboard — Pricing Preview UI
 * Lets guides enter test inputs (date, party size) and see a full price
 * breakdown with modifier effects. Quick scenario buttons pre-fill inputs.
 *
 * POST /api/v1/guides/experiences/:experienceId/pricing/preview
 */

import React, { useState } from 'react';
import { Calculator, Loader2, AlertCircle, Zap } from 'lucide-react';
import { previewPricing } from '../../../services/guidePricingApi';
import { formatCentsToDollars } from '../../../utils/formHelpers';

function getNextDayOfWeek(dayIndex) {
  const d = new Date();
  const diff = (dayIndex - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function getDateDaysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const SCENARIOS = [
  {
    label: 'Weekend booking',
    apply: () => ({ tripDate: getNextDayOfWeek(6) }),
    testId: 'scenario-weekend'
  },
  {
    label: 'Weekday booking',
    apply: () => ({ tripDate: getNextDayOfWeek(1) }),
    testId: 'scenario-weekday'
  },
  {
    label: 'Group of 6+',
    apply: () => ({ partySize: '6' }),
    testId: 'scenario-group'
  },
  {
    label: 'Early bird (30 days out)',
    apply: () => ({ tripDate: getDateDaysFromNow(30) }),
    testId: 'scenario-earlybird'
  }
];

export default function PricingPreviewPanel({ experienceId }) {
  const [inputs, setInputs] = useState({
    tripDate: '',
    partySize: '2',
    promoCode: ''
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateInput = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const applyScenario = (scenario) => {
    const values = scenario.apply();
    setInputs(prev => ({ ...prev, ...values }));
  };

  const handlePreview = async (e) => {
    e.preventDefault();

    if (!inputs.tripDate) {
      setError('Trip date is required');
      return;
    }

    const partySize = parseInt(inputs.partySize, 10);
    if (!partySize || partySize < 1) {
      setError('Party size must be at least 1');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        tripDate: new Date(inputs.tripDate).toISOString(),
        partySize
      };

      if (inputs.promoCode.trim()) {
        payload.promoCode = inputs.promoCode.trim();
      }

      const res = await previewPricing(experienceId, payload);
      setPreview(res.data);
    } catch (err) {
      setError(err.message || 'Failed to generate preview');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="pricing-preview-panel">
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="h-5 w-5 text-emerald-600" />
        <h3 className="text-lg font-semibold">Preview Pricing</h3>
      </div>

      {/* Simulation Disclaimer */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="preview-disclaimer">
        This is a preview and does not affect real bookings.
      </div>

      {/* Input Form */}
      <form onSubmit={handlePreview} data-testid="preview-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trip Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={inputs.tripDate}
              onChange={(e) => updateInput('tripDate', e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              data-testid="preview-trip-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Party Size <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={inputs.partySize}
              onChange={(e) => updateInput('partySize', e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              data-testid="preview-party-size"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Promo Code
            </label>
            <input
              type="text"
              value={inputs.promoCode}
              onChange={(e) => updateInput('promoCode', e.target.value.toUpperCase())}
              placeholder="Optional"
              className="w-full border rounded-md px-3 py-2 text-sm uppercase"
              data-testid="preview-promo-code"
            />
          </div>
        </div>

        {/* Quick Scenario Buttons */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="scenario-buttons">
          {SCENARIOS.map(scenario => (
            <button
              key={scenario.testId}
              type="button"
              onClick={() => applyScenario(scenario)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
              data-testid={scenario.testId}
            >
              <Zap className="h-3 w-3" />
              {scenario.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-2 bg-red-50 text-red-700 rounded text-sm flex items-center gap-2" data-testid="preview-error">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="preview-submit-btn"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Calculate Preview
        </button>
      </form>

      {/* Price Breakdown */}
      {preview && (
        <div className="mt-6 border-t pt-4" data-testid="preview-breakdown">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Price Breakdown</h4>

          <div className="space-y-2">
            {/* Base Rate */}
            <div className="flex justify-between text-sm" data-testid="breakdown-base">
              <span className="text-gray-600">Base Rate ({preview.pricingModelType})</span>
              <span className="font-medium">{formatCentsToDollars(preview.baseRateCents)}</span>
            </div>

            {/* Modifier Adjustments */}
            {preview.modifierAdjustments && preview.modifierAdjustments.length > 0 && (
              <div data-testid="breakdown-modifiers">
                {preview.modifierAdjustments.map((mod, idx) => (
                  <div key={idx} className="flex justify-between text-sm pl-4" data-testid="modifier-line">
                    <span className="text-gray-500">
                      {mod.name}
                      {mod.reason && <span className="text-xs text-gray-400 ml-1">({mod.reason})</span>}
                    </span>
                    <span className={`font-medium ${mod.adjustmentCents < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {mod.adjustmentCents >= 0 ? '+' : ''}{formatCentsToDollars(mod.adjustmentCents)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Subtotal After Modifiers */}
            {preview.subtotalAfterModifiersCents !== undefined && (
              <div className="flex justify-between text-sm border-t pt-1">
                <span className="text-gray-600">Subtotal after modifiers</span>
                <span className="font-medium">{formatCentsToDollars(preview.subtotalAfterModifiersCents)}</span>
              </div>
            )}

            {/* Add-ons */}
            {preview.addonsCents > 0 && (
              <div className="flex justify-between text-sm" data-testid="breakdown-addons">
                <span className="text-gray-600">Add-ons</span>
                <span className="font-medium">{formatCentsToDollars(preview.addonsCents)}</span>
              </div>
            )}

            {/* Promotion Discount */}
            {preview.promotionDiscountCents > 0 && (
              <div className="flex justify-between text-sm text-green-600" data-testid="breakdown-promotion">
                <span>
                  Promotion discount
                  {preview.promotionDetails?.name && ` (${preview.promotionDetails.name})`}
                </span>
                <span className="font-medium">-{formatCentsToDollars(preview.promotionDiscountCents)}</span>
              </div>
            )}

            {/* Platform Fee */}
            <div className="flex justify-between text-sm text-gray-500" data-testid="breakdown-platform-fee">
              <span>Platform fee ({preview.platformFeePercent}%)</span>
              <span>{formatCentsToDollars(preview.platformFeeCents)}</span>
            </div>

            {/* Total */}
            <div className="flex justify-between text-base font-semibold border-t pt-2 mt-2" data-testid="breakdown-total">
              <span>Customer Total</span>
              <span>{formatCentsToDollars(preview.totalCents)}</span>
            </div>

            {/* Guide Receives */}
            <div className="flex justify-between text-sm text-emerald-600" data-testid="breakdown-guide-receives">
              <span>You receive</span>
              <span className="font-medium">{formatCentsToDollars(preview.guideReceivesCents)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Empty state before first preview */}
      {!preview && !loading && !error && (
        <div className="mt-4 text-center text-gray-400 text-sm" data-testid="preview-empty-state">
          Enter trip details above and click &quot;Calculate Preview&quot; to see pricing.
        </div>
      )}
    </div>
  );
}
